import { CancelledError } from '../cancel';

/**
 * Ожидание готовности задачи: опрос get_status до READY.
 *
 * Общий для файлового и стерео-конвейеров — они отвечают одинаково
 * (`status: PROCESSING | READY | ERROR`), различается только адрес результата.
 *
 * 🔴 `signal` — общий с экраном объект `{ aborted }`. Цикл сам по себе не
 * кончается, и без проверки нажатие «Отменить» чистило экран, а опрос
 * продолжал ходить на сервер и в конце подставлял результат отменённой
 * задачи поверх пустой страницы.
 *
 * 🔴 Этап — по статусу сервера, а не по факту запуска. Пока задача ждёт
 * свободный слот (PENDING, а до него UPLOADING — сервер ещё разбирает файл),
 * это «Очередь». До 21.10.2026 экран сразу писал «Распознаём речь · 0 %» и
 * держал так всё ожидание: 16 минут в очереди diarize человек прочитал как
 * «зависает на этапе распознавания» (обращение 278cc381).
 */

export const POLL_INTERVAL_MS = 3000;

const WAITING = new Set(['CREATED', 'UPLOADING', 'PENDING']);

export async function pollUntilReady(
  { taskId, baseUrl }, { get, onProgress, onStage, intervalMs = POLL_INTERVAL_MS, sleep, signal },
) {
  const wait = sleep || ((ms) => new Promise((r) => setTimeout(r, ms)));
  for (;;) {
    if (signal?.aborted) throw new CancelledError();
    const { data } = await get(`${baseUrl}/get_status?task_id=${taskId}`);
    if (data.status === 'READY') return data;
    // Ошибку показываем словами сервиса: он знает причину, а «что-то пошло
    // не так» отправляет человека гадать.
    if (data.status === 'ERROR') throw new Error(data.error || 'Ошибка обработки записи');
    // Отменили не здесь (своя отмена вышла бы по signal выше): сервис склеивает
    // одинаковый файл той же учётки в одну задачу, и «Отменить» в другой вкладке
    // отменяет и эту. Без выхода опрос шёл бы до закрытия вкладки (ревью Fable 22.10).
    if (data.status === 'CANCELLED') {
      throw new Error('Распознавание отменено — возможно, в другой вкладке с этим же файлом. Загрузите запись ещё раз.');
    }
    // Стерео: dual держит PROCESSING, пока каналы ждут слот в распознавании, и говорит
    // об этом полем waiting_asr (22.10.2026).
    if (onStage) onStage(WAITING.has(data.status) || data.waiting_asr === true ? 'Очередь' : 'Распознавание');
    if (onProgress && typeof data.progress === 'number') onProgress(data.progress);
    await wait(intervalMs);
  }
}
